import EVENTS, { events } from '../events.js';
import triggerEvent from '../triggerEvent.js';

/**
 * This module deals with caching image textures in VRAM for WebGL
 *
 * @module WebGLTextureCache
 */

const imageCache = {};

const cachedImages = [];

let maximumSizeInBytes = 1024 * 1024 * 256; // 256 MB
let cacheSizeInBytes = 0;

/**
 * Get cache info
 *
 * @returns {object} Cache info
 */
function getCacheInfo() {
  return {
    maximumSizeInBytes,
    cacheSizeInBytes,
    numberOfImagesCached: cachedImages.length,
  };
}

/**
 * Cache size has been exceeded, create list of images sorted by timeStamp
 * So we can purge the least recently used image
 *
 * @param {*} a - timestamp a
 * @param {*} b - timestamp b
 * @returns {number} result
 */
function compare(a, b) {
  if (a.timeStamp > b.timeStamp) {
    return -1;
  }
  if (a.timeStamp < b.timeStamp) {
    return 1;
  }

  return 0;
}

/**
 * Purge cache if necessary
 */
function purgeCacheIfNecessary() {
  // If max cache size has not been exceeded, do nothing
  if (cacheSizeInBytes <= maximumSizeInBytes) {
    return;
  }

  cachedImages.sort(compare);

  // Remove images as necessary
  while (cacheSizeInBytes > maximumSizeInBytes) {
    const lastCachedImage = cachedImages[cachedImages.length - 1];

    cacheSizeInBytes -= lastCachedImage.sizeInBytes;
    delete imageCache[lastCachedImage.imageId];
    cachedImages.pop();

    triggerEvent(events, EVENTS.WEBGL_TEXTURE_REMOVED, { imageId: lastCachedImage.imageId });
  }

  const cacheInfo = getCacheInfo();

  triggerEvent(events, EVENTS.WEBGL_TEXTURE_CACHE_FULL, cacheInfo);
}

/**
 * Set maximum size bytes
 *
 * @param {number} numBytes Number of bytes
 */
function setMaximumSizeBytes(numBytes) {
  if (numBytes === undefined) {
    throw new Error('setMaximumSizeBytes: parameter numBytes must not be undefined');
  }
  if (numBytes.toFixed === undefined) {
    throw new Error('setMaximumSizeBytes: parameter numBytes must be a number');
  }

  maximumSizeInBytes = numBytes;
  purgeCacheIfNecessary();
}

/**
 * Put image texture to image object
 *
 * @param {object} image Image object
 * @param {object} imageTexture Image texture
 */
function putImageTexture(image, imageTexture) {
  const imageId = image.imageId;

  if (image === undefined) {
    throw new Error('putImageTexture: image must not be undefined');
  }

  if (imageId === undefined) {
    throw new Error('putImageTexture: imageId must not be undefined');
  }

  if (imageTexture === undefined) {
    throw new Error('putImageTexture: imageTexture must not be undefined');
  }

  if (Object.prototype.hasOwnProperty.call(imageCache, imageId) === true) {
    throw new Error('putImageTexture: imageId already in cache');
  }

  const cachedImage = {
    imageId,
    imageTexture,
    timeStamp: new Date(),
    sizeInBytes: imageTexture.sizeInBytes,
  };

  imageCache[imageId] = cachedImage;
  cachedImages.push(cachedImage);

  if (imageTexture.sizeInBytes === undefined) {
    throw new Error('putImageTexture: imageTexture.sizeInBytes must not be undefined');
  }
  if (imageTexture.sizeInBytes.toFixed === undefined) {
    throw new Error('putImageTexture: imageTexture.sizeInBytes is not a number');
  }
  cacheSizeInBytes += cachedImage.sizeInBytes;
  purgeCacheIfNecessary();
}

/**
 * Get image texture
 *
 * @param {string} imageId - Image ID
 * @returns {*} Image texture
 */
function getImageTexture(imageId) {
  if (imageId === undefined) {
    throw new Error('getImageTexture: imageId must not be undefined');
  }
  const cachedImage = imageCache[imageId];

  if (cachedImage === undefined) {
    return undefined;
  }

  // Bump time stamp for cached image
  cachedImage.timeStamp = new Date();

  return cachedImage.imageTexture;
}

/**
 * Remove image texture
 *
 * @param {string} imageId - Image ID
 * @returns {*} Image texture
 */
function removeImageTexture(imageId) {
  if (imageId === undefined) {
    throw new Error('removeImageTexture: imageId must not be undefined');
  }
  const cachedImage = imageCache[imageId];

  if (cachedImage === undefined) {
    throw new Error('removeImageTexture: imageId must not be undefined');
  }
  cachedImages.splice(cachedImages.indexOf(cachedImage), 1);
  cacheSizeInBytes -= cachedImage.sizeInBytes;
  delete imageCache[imageId];

  return cachedImage.imageTexture;
}

/**
 * Purge texture cache
 */
function purgeCache() {
  while (cachedImages.length > 0) {
    const removedCachedImage = cachedImages.pop();

    delete imageCache[removedCachedImage.imageId];
  }
  cacheSizeInBytes = 0;
}

const textureCache = {
  purgeCache,
  getImageTexture,
  putImageTexture,
  removeImageTexture,
  setMaximumSizeBytes,
};

export default textureCache;
